/* eslint-disable no-restricted-properties */
import React from 'react';
import invert from 'lodash/invert';
import keyBy from 'lodash/keyBy';
import { createBrowserHistory } from 'history';
import moment from 'moment';
import kebabCase from 'lodash/kebabCase';
import upperFirst from 'lodash/upperFirst';

import { GenericContextType } from 'types/generic-context';

import type { EntityActionName } from 'types/common-types';

export { default as urlPattern } from '../containers/DraftEditor/regexWebUrl';

export const hostnames = {
  local: '127.0.0.1',
  localhost: 'localhost',
  mylocal: 'mylocal.com',
  devMylocal: 'dev.mylocal.com',
  dev: 'dev.eva.ai',
  uat: 'uat.eva.ai',
};
export const environments = {
  prd: 'prd',
  uat: 'uat',
  dev: 'dev',
  local: 'local',
  test: 'test',
};

export const localHostnames = [hostnames.local, hostnames.localhost, hostnames.mylocal, hostnames.devMylocal];

export let safeLocalStorage = {};
try {
  // @ts-expect-error :hmmm
  const accessCheck = localStorage.tenant; // eslint-disable-line @typescript-eslint/no-unused-vars
  safeLocalStorage = localStorage;
} catch (err) {
  // Do nothing;
}

let letBaseEndpoint: any;
// @ts-expect-error global variable
let letCompanyPrefix = safeLocalStorage.tenant || 'dev';

export const devMode = hostnames.local === window.location.hostname;
export const extendedDevMode = devMode || hostnames.localhost === window.location.hostname;

if (devMode) {
  letBaseEndpoint = 'http://localhost:8080';
} else if (window.location.hostname === hostnames.localhost) {
  letBaseEndpoint = `https://${letCompanyPrefix}.api.eva.ai`;
} else if (window.location.hostname.endsWith('mylocal.com')) {
  letBaseEndpoint = `http://${window.location.hostname.replace('mylocal.com', 'api.mylocal.com')}:8080`;
} else {
  letCompanyPrefix = window.location.hostname.split('.')[0];
  letBaseEndpoint = `https://${letCompanyPrefix}.api.eva.ai`;
}

export const localDevelopment = window.location.hostname === hostnames.local;
export const baseEndpoint = letBaseEndpoint;
export const companyPrefix = letCompanyPrefix;
export const endpoint = `${baseEndpoint}/api`;
export const filesEndpoint = 'https://s3-eu-west-1.amazonaws.com';
export const iframeMode = window.self !== window.top;
export const caches = {};
export const messagesPageSize = 30;
export const maxFileSize = 10485760;
const login = 'login';

// TODO Switch to userTypes, since always used as userType
export const accountTypes = {
  agent: 1,
  contact: 2,
  candidate: 3,
};

export const userTypes = {
  agent: 1,
  contact: 2,
  candidate: 3,
};
export const invertedUserTypes = invert(userTypes);
export const typingDelay = 5000;
export const messagesLevelsTypes = {
  success: 'success',
  danger: 'danger',
  info: 'info',
};
export const messages = {
  A002: {
    type: messagesLevelsTypes.success,
    label: 'Welcome back!',
  },
  A003: {
    type: messagesLevelsTypes.success,
    label: 'Thanks for joining!',
  },
  A004: {
    type: messagesLevelsTypes.danger,
    label: 'Oh no! Facebook had a problem! Please try again.',
  },
  A006: {
    type: messagesLevelsTypes.danger,
    label: 'Oh no! Google had a problem! Please try again.',
  },
  A008: {
    type: messagesLevelsTypes.danger,
    label: "Oh no! We can't find your email. Please try again.",
  },
  A009: {
    type: messagesLevelsTypes.success,
    label: 'Thanks, we have sent you an email containing instruction to reset your password.',
  },
  A010: {
    type: messagesLevelsTypes.danger,
    label: 'Oh no! There was a problem with your registration. Please try again.',
  },
  A011: {
    type: messagesLevelsTypes.success,
    label: 'Thanks, we have successfully reset your password. You can now sign in.',
  },
};
export const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
export const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line max-len

export const workflowsCandidateCodes = {
  invited: 'invited',
  applied: 'applied',
  declined: 'declined',
  declined1: 'declined1',
  declined2: 'declined2',
  qualifying: 'qualifying',
  qualifying2: 'qualifying2',
  interviewing: 'interviewing',
  offered: 'offered',
  placed: 'placed',
};
export const leftMenuModes = {
  menu: 1,
  signIn: 2,
  restorePassword: 3,
};
export const googleApiKey = 'AIzaSyBQ0q3vMP0fgH_UZZdAAecUem8Mnjylmk4';
const aiNodesKeys = ['cv', 'tnc', 'allGood', 'finish'];
export const aiNodes = aiNodesKeys.reduce(
  (prev, cur) => ({
    ...prev,
    [cur]: cur,
  }),
  {},
);
export const jobStates = {
  active: 'active',
  lead: 'lead',
  onHold: 'onHold',
  cancelled: 'cancelled',
  closed: 'closed',
  filled: 'filled',
};
export const messagesTypes = {
  text: 1,
  genericSystem: 2,
  call: 3,
  sentEmail: 4,
  receivedEmail: 5,
  pipelineStatusChange: 6,
  sms: 7,
  note: 8,
  facebookMessage: 9,
  qnaMissedAnswer: 10,
};
export const jobsStatuses = {
  active: {
    ids: [jobStates.active, jobStates.lead],
    icon: 'lnr-checkmark-circle',
    color: '#5cb85c',
  },
  onHold: {
    ids: [jobStates.onHold],
    icon: 'lnr-warning',
    color: '#f0ad4e',
  },
  closed: {
    ids: [jobStates.cancelled, jobStates.closed, jobStates.filled],
    icon: 'lnr-cross-circle',
    color: '#d9534f',
  },
  other: {
    ids: [],
    icon: 'lnr-question-circle',
    color: '#7A8E92',
  },
};

export const candidateRegistrationWorkflowCodes = {
  invited: 'invited',
  notInvited: 'not_invited',
  registering: 'registering',
  registered: 'registered',
  hangedRegistration: 'hanged_registration',
};

export const chromeExtensionIds = ['kkbenjalnbdpknahknlnieciokkpfijg', 'dbbphjhklckdnlapiiecnagcmdhkcjgk'];

export const emailTypes = {
  primary: 1,
  secondary: 2,
  [login]: 3,
};

export const emailTemplates = [];
export const messageTimeFormat = 'h:mm a';
export const sentEmailStatuses = {
  sending: {
    id: 0,
  },
  sent: {
    id: 1,
  },
  ignore: {
    id: 2,
  },
  error: {
    id: 3,
  },
  delivered: {
    id: 4,
    eventType: 'Delivered',
  },
  bounced: {
    id: 5,
    eventType: 'Bounced',
  },
  opened: {
    id: 6,
    eventType: 'Opened',
  },
  clicked: {
    id: 7,
    eventType: 'LinkClicked',
  },
  transient: {
    id: 8,
    eventType: 'Transient',
  },
};
export const cpPaneModes = {
  cv: 'cv',
  files: 'files',
  chat: 'chat',
};
export const containersContexts = {
  chat: 'chat',
  match: 'match',
  screenMeet: 'screenMeet',
  search: 'search',
  candidates: 'candidates',
  entities: 'entities',
};
export const imagesExtensions = /\.(gif|jpg|jpeg|tiff|png|webp)$/;
export const cvExtensions = /\.(doc|docx|dot|rtf|pdf|odt|txt|htm|html)$/;
export const acceptImagesExtensions = '.gif,.jpg,.jpeg,.tiff,.png,.webp';
export const acceptCvExtensions = '.doc,.docx,.dot,.rtf,.pdf,.odt,.txt,.htm,.html';

export const acceptImageTypes = ['image/gif', 'image/jpeg', 'image/tiff', 'image/png', 'image/webp'];

export const acceptCvTypes = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/rtf',
  'application/pdf',
  'application/vnd.oasis.opendocument.text',
  'text/plain',
  'text/html',
];

export const pageSizes = [5, 10, 20, 50, 100];
export const defaultPageSize = pageSizes[2];
export const base64Prefix = /^data.+;base64,/;
export const phoneTypes = {
  cell: 1,
  home: 2,
  work: 3,
};
export const noteTypes = {
  panel: 'panel',
  chat: 'chat',
  pdf: 'pdf',
};
export const noteTypesCodes = {
  [noteTypes.panel]: 1,
  [noteTypes.chat]: 2,
  [noteTypes.pdf]: 3,
};
export const zeroTime = '00:00';
export const endTime = '24:00';
export const availabilitySlotsNumber = 48;
export const availabilitySlotsMinutes = 30;
export const availabilityDayStart = (60 / availabilitySlotsMinutes) * 6; // 6 AM
export const conversationStates = {
  stale: 'stale',
  unread: 'unread',
};
export const yearFirstDateFormat = 'YYYY-MM-DD';
export const hoursMinutesDateFormat = 'HH:mm';
export const overlayTriggerClick = ['click'];
export const overlayTriggerHoverFocus = ['hover', 'focus'];
export const overlayTriggerHover = ['hover'];
export const calendarPeriodTemplates = ['month', 'quarter', 'year'];
export const emailTypesKeys = Object.keys(emailTypes).filter((key) => key !== login);
export const phoneTypesKeys = Object.keys(phoneTypes);
export const fullCalendarDistUrl =
  'https://rawgit.com/fullcalendar/fullcalendar/354812a73980168681506274d90810da85f8f573/dist';
export const schedulerDistUrl =
  'https://rawgit.com/fullcalendar/fullcalendar-scheduler/a1e0dbd8a24a936b980c5ab624f8bc0181f8f97c/dist';

export const routesNames = {
  portals: 'portals',
};

export const pipelineActionTypes = {
  email: 0,
};

export const agentPrefix = '/agent';
export const candidatePrefix = '/candidate';
export const messengerPrefix = '/messenger';
export const authPrefix = '/auth/';
export const skillTaxonomiesPrefix = '/skill-taxonomies';

export const agencyTypes = {
  agency: 'agency',
  internal: 'internal',
};
export const defaultBulkSelection = {
  bulkSelected: {},
  bulkErrors: {},
  bulkPaneCandidates: [],
};
export const emailTemplateTypes = {
  candidateManual: 'candidateManual',
  contactManual: 'contactManual',
  system: 'system',
  reengagement: 'reengagement',
  evaBotCampaign: 'evaBotCampaign',
  businessWorkflows: 'businessWorkflows',
  systemNotifications: 'systemNotifications',
  customService: 'customService',
};
export const intermediate = 'intermediate';
export const employmentTypes = {
  permanentPartTime: 'permanent_part_time',
  permanentFullTime: 'permanent_full_time',
  interimTemporary: 'interim_temporary',
  fixedTermContract: 'fixed_term_contract',
};
export const minPasswordLength = 8;
export const errorTypes = {
  missingField: 'missingField',
  serverError: 'serverError',
  permissionError: 'permissionError',
  quotaError: 'quotaError',
  quotaNotification: 'quotaNotification',
  errorWithoutPath: 'errorWithoutPath',
  wrongErrorFormat: 'wrongErrorFormat',
};

export const socialMediaProviders = ['linkedIn', 'facebook'];
export const entitiesTypes = {
  candidates: 'candidates',
  externalCandidates: 'externalCandidates',
  companies: 'companies',
  contacts: 'contacts',
  agents: 'agents',
  jobs: 'jobs',
  jobRequests: 'jobRequests',
  userGroups: 'userGroups',
  staticPages: 'staticPages',
  skillTaxonomies: 'skillTaxonomies',
  evaSkills: 'evaSkills',
  evaBotCampaigns: 'evaBotCampaigns',
  callTypes: 'callTypes',
  callOutcomes: 'callOutcomes',
  businessWorkflows: 'businessWorkflows',
  pipelineWorkflows: 'pipelineWorkflows',
  pipelines: 'pipelines',
  pipelinesCrud: 'pipelinesCrud',
  entityLists: 'entityLists',
  tags: 'tags',
  users: 'users',
  emailTemplates: 'emailTemplates',
  institutions: 'institutions',
  dashboards: 'dashboards',
} as const;
export const entityTypeIds = {
  candidates: 1,
  companies: 3,
  contacts: 4,
  jobs: 2,
  agents: 7,
  pipelines: 9,
  skills: 15,
  jobRequests: 19,
};
export const emailTemplateEditorModes = {
  html: 'html',
  simple: 'simple',
  unlayer: 'unlayer',
};
export const communicationDirections = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};
export const omnichatChannels = [
  {
    label: 'sms',
    icon: 'lnr lnr-smartphone',
  },
  {
    label: 'facebook',
    icon: 'fa fa-facebook',
  },
  {
    label: 'webchat',
    icon: 'll-icon ll-logo-eva-small',
  },
  {
    label: 'email',
    icon: 'lnr lnr-envelope',
  },
];
export const supportedLocales = {
  en: 'en',
  fr: 'fr',
};
export const providersTypes = {
  luckylink: 0,
  totaljobs: 1,
  caterer: 2,
  reed: 3,
  // indeed: 4, ??
  jobsite: 18,
  totaljobsGroup: 19,
  eMedCareers: 20,
  cvLibrary: 21,
  applications: 38,
  gpt: 39,
  applicationsGpt: 40,
} as const;

export const providersLabels = {
  [providersTypes.luckylink]: 'Internal Database',
  [providersTypes.totaljobs]: 'Totaljobs',
  [providersTypes.caterer]: 'Caterer',
  [providersTypes.reed]: 'Reed.co.uk',
  [providersTypes.jobsite]: 'Jobsite',
  [providersTypes.eMedCareers]: 'eMedCareers',
  [providersTypes.totaljobsGroup]: 'Totaljobs Group',
  [providersTypes.cvLibrary]: 'CV-Library',
  [providersTypes.applications]: 'Applications',
  [providersTypes.gpt]: 'EVA - AI Assistant',
  [providersTypes.applicationsGpt]: 'EVA - AI Assistant for Applications',
};
export const yellowHighlight = '#fdf4f4';
export const messengerQueryParams = ['name', 'filterState', 'agents'];

export const subscriptionTypes = {
  legitimateInterest: 'legitimateInterest',
  consentObtained: 'consentObtained',
  unsubscribeAutomatic: 'unsubscribeAutomatic',
  unsubscribeEmails: 'unsubscribeEmails',
  unsubscribeCommunications: 'unsubscribeCommunications',
};
export const fileExtensions = {
  image: 'image',
  document: 'document',
  txt: 'txt',
  pdf: 'pdf',
  rtf: 'rtf',
};
export const locationKeys = ['street', 'city', 'state', 'postalCode', 'note'];
export const milesInKm = 0.621371;
export const dayOfMonthFormat = 'Do MMM';
export const backendDateFormat = 'YYYY.MM.DD';
export const monthsOptions = [];

const monthMoment = moment().month(0);
for (let shift = 1; shift <= 12; shift++) {
  monthsOptions.push({
    value: shift,
    label: monthMoment.format('MMMM'),
  });
  monthMoment.add(1, 'month');
}
export const jsonSchemaTypes = {
  string: 'string',
  number: 'number',
  integer: 'integer',
  boolean: 'boolean',
  null: 'null',
};
export const recognitionStatuses = {
  correct: 'correct',
  notCorrect: 'notCorrect',
};

export const workflowTransitionEntryPoints = {
  addingInMatch: 'addingInMatch',
  addingInMatchExternal: 'addingInMatchExternal',
  addingJobManuallyFromPanel: 'addingJobManuallyFromPanel',
  addingJobManuallyFromCandidatePipelines: 'addingJobManuallyFromCandidatePipelines',
  addingFromCvInbox: 'addingFromCvInbox',
  applyingFromFacebook: 'applyingFromFacebook',
  applyingFromPublicJobLink: 'applyingFromPublicJobLink',
  applyingFromTeamTailor: 'applyingFromTeamTailor',
  recommendation: 'recommendation',
  rejectingInMatch: 'rejectingInMatch',
  newApplicationEmail: 'newApplicationEmail',
  newApplicantInvitedToChat: 'newApplicantInvitedToChat',
  addingManually: 'addingManually',
  addingAutomatically: 'addingAutomatically',
  applyingFromHeadHunter: 'applyingFromHeadHunter',
  applyingFromSuperjob: 'applyingFromSuperjob',
  applyingFromChatbot: 'applyingFromChatbot',
  decliningFromChatbot: 'decliningFromChatbot',
  applyingFromJobPortal: 'applyingFromJobPortal',
  applyingFromIndeed: 'applyingFromIndeed',
  manualMove: 'manualMove',
};
export const grayColor = '#B2C3CB';

export const attachmentTypeCodes = {
  noType: 'noType',
  drivingLicense: 'drivingLicense',
  proofOfIdentity: 'proofOfIdentity',
  rightToWork: 'rightToWork',
  proofOfAddress: 'proofOfAddress',
  reference: 'reference',
  diplomaCertification: 'diplomaCertification',
};

export const workflowTransitionEntryPointsLabels = {
  [workflowTransitionEntryPoints.addingInMatch]: 'Adding in Match',
  [workflowTransitionEntryPoints.addingInMatchExternal]: 'Adding in Match (external)',
  [workflowTransitionEntryPoints.addingJobManuallyFromPanel]: 'Adding job manually from job panel',
  [workflowTransitionEntryPoints.addingJobManuallyFromCandidatePipelines]:
    'Adding job manually from candidate pipelines',
  [workflowTransitionEntryPoints.addingFromCvInbox]: 'Adding from CV inbox',
  [workflowTransitionEntryPoints.applyingFromFacebook]: 'Applying from Facebook',
  [workflowTransitionEntryPoints.applyingFromPublicJobLink]: 'Applying from Public Job link',
  [workflowTransitionEntryPoints.applyingFromTeamTailor]: 'Applying from Teamtailor',
  [workflowTransitionEntryPoints.recommendation]: 'Recommendation',
  [workflowTransitionEntryPoints.rejectingInMatch]: 'Rejecting in Match',
  [workflowTransitionEntryPoints.addingManually]: 'Adding manually',
  [workflowTransitionEntryPoints.addingAutomatically]: 'Adding automatically',
  [workflowTransitionEntryPoints.applyingFromHeadHunter]: 'Applying from HeadHunter',
  [workflowTransitionEntryPoints.applyingFromSuperjob]: 'Applying from Superjob',
  [workflowTransitionEntryPoints.applyingFromChatbot]: 'Applying from Chatbot',
  [workflowTransitionEntryPoints.decliningFromChatbot]: 'Declining from Chatbot',
  [workflowTransitionEntryPoints.applyingFromJobPortal]: 'Applying from Job Portal',
  [workflowTransitionEntryPoints.applyingFromIndeed]: 'Applying from Indeed',
  [workflowTransitionEntryPoints.manualMove]: 'Manual move',
};
export const missingValueError = 'At least one is required';
export const fromTo = ['from', 'to'];

export const entityTypes = {
  companyLogo: {
    name: 'Company logo',
    code: 'companyLogo',
    pluralCode: 'companyLogos',
    entityTypeId: 5,
  },
  location: {
    name: 'Location',
    code: 'location',
    pluralCode: 'locations',
    entityTypeId: 6,
  },
  cv: {
    name: 'Resume',
    code: 'cv',
    pluralCode: 'cv',
    entityTypeId: 8,
  },
  candidatePhoto: {
    name: 'Candidate photo',
    code: 'candidatePhoto',
    pluralCode: 'candidatePhotos',
    entityTypeId: 10,
  },
  cvInbox: {
    name: 'CvInbox',
    code: 'cvInbox',
    pluralCode: 'cvInboxes',
    entityTypeId: 11,
  },
  postmark: {
    name: 'Postmark',
    code: 'postmark',
    pluralCode: 'postmark',
    entityTypeId: 12,
  },
  nylas: {
    name: 'Nylas',
    code: 'nylas',
    pluralCode: 'nylas',
    entityTypeId: 13,
  },
  activityNote: {
    name: 'Activity note',
    code: 'activityNote',
    pluralCode: 'activityNotes',
    entityTypeId: 14,
  },
  skill: {
    name: 'Skill',
    code: 'skill',
    pluralCode: 'skills',
    entityTypeId: 15,
  },
  externalEmail: {
    name: 'External email',
    code: 'externalEmail',
    pluralCode: 'externalEmails',
    entityTypeId: 16,
  },
  attachmentType: {
    name: 'Attachment type',
    code: 'attachmentType',
    pluralCode: 'attachmentTypes',
    entityTypeId: 17,
  },
  entityList: {
    name: 'Entity list',
    code: 'entityList',
    pluralCode: 'entityLists',
    entityTypeId: 18,
  },
  emailTemplates: {
    entitiesType: entitiesTypes.emailTemplates,
    name: 'Email template',
    code: 'emailTemplate',
    pluralCode: 'emailTemplates',
  },
  candidate: {
    entityIdKey: 'userId',
    endpointPath: '/candidates',
    entitiesType: entitiesTypes.candidates,
    entitiesLabel: 'Candidates',
    entityType: 'candidate',
    name: 'Candidate',
    code: 'candidate',
    pluralCode: 'candidates',
    entityTypeId: 1,
  },
  job: {
    entityType: 'job',
    entityIdKey: 'jobId',
    entitiesLabel: 'Jobs',
    endpointPath: '/jobs',
    entitiesType: entitiesTypes.jobs,
    name: 'Job',
    code: 'job',
    pluralCode: 'jobs',
    entityTypeId: 2,
  },
  company: {
    entityType: 'company',
    entityIdKey: 'companyId',
    entitiesLabel: 'Companies',
    endpointPath: '/companies',
    entitiesType: entitiesTypes.companies,
    name: 'Company',
    code: 'company',
    pluralCode: 'companies',
    entityTypeId: 3,
  },
  contact: {
    entityType: 'contact',
    entityIdKey: 'userId',
    entitiesLabel: 'Contacts',
    endpointPath: '/contacts',
    entitiesType: entitiesTypes.contacts,
    name: 'Contact',
    code: 'contact',
    pluralCode: 'contacts',
    entityTypeId: 4,
  },
  agent: {
    entityType: 'agent',
    entityIdKey: 'userId',
    entitiesLabel: 'HR Agents',
    endpointPath: '/agents',
    entitiesType: entitiesTypes.agents,
    name: 'Agent',
    code: 'agent',
    pluralCode: 'agents',
    entityTypeId: 7,
  },
  pipeline: {
    entityIdKey: 'pipelineId',
    endpointPath: '/pipelines',
    entitiesType: entitiesTypes.pipelines,
    entitiesLabel: 'Pipelines',
    entityType: 'pipeline',
    name: 'Pipeline',
    code: 'pipeline',
    pluralCode: 'pipelines',
    entityTypeId: 9,
  },
  jobRequest: {
    entityType: 'jobRequest',
    entityIdKey: 'jobRequestId',
    entitiesLabel: 'Job requests',
    endpointPath: '/job-requests',
    entitiesType: entitiesTypes.jobRequests,
    name: 'Job Request',
    code: 'jobRequest',
    pluralCode: 'jobRequests',
    entityTypeId: 19,
  },
  evaSkill: {
    entityIdKey: 'code',
    endpointPath: '/eva/skills',
    entitiesType: entitiesTypes.evaSkills,
    entitiesLabel: 'Basic Skills',
    entityType: 'evaSkill',
    name: 'Eva skill',
    code: 'evaSkill',
    pluralCode: 'evaSkills',
    entityTypeId: 20,
  },
  skillTaxonomy: {
    entityIdKey: 'skillTaxonomyId',
    entitiesType: entitiesTypes.skillTaxonomies,
    endpointPath: '/skill-taxonomies',
    entitiesLabel: 'Skill taxonomies',
    entityType: 'skillTaxonomy',
    name: 'skill Taxonomy',
    code: 'skillTaxonomy',
    pluralCode: 'skillTaxonomies',
    entityTypeId: 21,
  },
  evaBotCampaigns: {
    entityIdKey: 'evaBotCampaignId',
    endpointPath: '/eva-bot-campaigns',
    entitiesType: entitiesTypes.evaBotCampaigns,
    entitiesLabel: 'Eva Bot Campaigns',
    entityType: 'evaBotCampaign',
    name: 'Eva bot campaign',
    code: 'evaBotCampaign',
    pluralCode: 'evaBotCampaigns',
  },
  staticPages: {
    entityIdKey: 'staticPageId',
    endpointPath: '/static-pages',
    entitiesType: entitiesTypes.staticPages,
    entitiesLabel: 'Static pages',
    entityType: 'staticPage',
    name: 'Static page',
    code: 'staticPage',
    pluralCode: 'staticPages',
  },
  dashboards: {
    entityIdKey: 'dashboardId',
    endpointPath: '/bi/dashboards',
    entitiesType: entitiesTypes.dashboards,
    entitiesLabel: 'Dashboards',
    entityType: 'dashboard',
    name: 'Dashboard',
    code: 'dashboard',
    pluralCode: 'dashboards',
  },
  userGroups: {
    entityType: 'userGroup',
    entityIdKey: 'userGroupId',
    entitiesLabel: 'User groups',
    endpointPath: '/authorisation/user-groups',
    entitiesType: entitiesTypes.userGroups,
    name: 'User Group',
    code: 'userGroup',
    pluralCode: 'userGroups',
  },
  businessWorkflows: {
    entityIdKey: 'workflowId',
    endpointPath: '/business-workflows/workflows',
    entitiesType: entitiesTypes.businessWorkflows,
    entitiesLabel: 'Business Workflows',
    entityType: 'businessWorkflow',
    name: 'Business Workflows',
    code: 'businessWorkflows',
    pluralCode: 'businessWorkflows',
  },
  /** @deprecated TODO remove in DEV-25601 */
  evaSkills: {
    entityIdKey: 'code',
    endpointPath: '/eva/skills',
    entitiesType: entitiesTypes.evaSkills,
    entitiesLabel: 'Basic Skills',
    entityType: 'evaSkill',
    name: 'Eva skill',
    code: 'evaSkill',
    pluralCode: 'evaSkills',
    entityTypeId: 20,
  },
  institutions: {
    entityIdKey: 'institutionId',
    endpointPath: '/whed/institutions',
    entitiesType: entitiesTypes.institutions,
    entitiesLabel: 'Institutions',
    entityType: 'institution',
  },
  pipelineCrud: {
    entityIdKey: 'pipelineId',
    endpointPath: '/pipelines',
    entitiesType: entitiesTypes.pipelinesCrud,
    entitiesLabel: 'Candidates',
    entityType: 'pipeline',
  },
  pipelineWorkflow: {
    entityIdKey: 'pipelineWorkflowId',
    endpointPath: '/pipelines/workflows',
    entitiesType: entitiesTypes.pipelineWorkflows,
    entitiesLabel: 'Pipeline Workflows',
    entityType: 'pipelineWorkflow',
  },
  externalCandidate: {
    entityIdKey: 'compositeId',
    endpointPath: '/candidates',
    entitiesType: entitiesTypes.externalCandidates,
    entitiesLabel: 'Candidates',
    entityType: 'candidate',
  },
  emailTemplatesCandidate: {
    entityIdKey: 'emailTemplateId',
    endpointPath: `/emails/templates/${kebabCase(emailTemplateTypes.candidateManual)}`,
    entitiesType: 'emailTemplatesCandidates',
    entitiesLabel: 'Email Templates',
    entityType: `emailTemplate${upperFirst(emailTemplateTypes.candidateManual)}`,
  },
  emailTemplatesContact: {
    entityIdKey: 'emailTemplateId',
    endpointPath: `/emails/templates/${kebabCase(emailTemplateTypes.contactManual)}`,
    entitiesType: 'emailTemplatesContacts',
    entitiesLabel: 'Email Templates',
    entityType: `emailTemplate${upperFirst(emailTemplateTypes.contactManual)}`,
  },
} as const;

export const workLocationPreferenceTypes = {
  remote: 'remote',
  travelToSite: 'travelToSite',
  officeBased: 'officeBased',
  partiallyRemote: 'partiallyRemote',
};
export const minMaxOptions = ['min', 'max'];

export const workflowTypeCodes = {
  pipelineFlow: 'pipelineFlow',
  candidateRegistrationFlow: 'candidateRegistrationFlow',
  candidateGeneralFlow: 'candidateGeneralFlow',
  webchatFlow: 'webchatFlow',
  smsFlow: 'smsFlow',
  fbFlow: 'fbFlow',
  interviewingFlow: 'interviewingFlow',
  interviewConfirmation: 'interviewConfirmation',
  jobFlow: 'jobFlow',
  dataMigrationFlow: 'dataMigrationFlow',
  evaBotCampaignFlow: 'evaBotCampaignFlow',
  jobRequestFlow: 'jobRequestFlow',
  placementFlow: 'placementFlow',
};

export const bwfEntityTypes = {
  candidate: {
    entitiesType: 'candidates',
    name: 'Candidate',
    code: 'candidate',
    pluralCode: 'candidates',
    entityTypeId: 1,
  },
  job: {
    entitiesType: 'jobs',
    name: 'Job',
    code: 'job',
    pluralCode: 'jobs',
    entityTypeId: 2,
    workflowTypeCode: workflowTypeCodes.jobFlow,
  },
  company: {
    entitiesType: 'companies',
    name: 'Company',
    code: 'company',
    pluralCode: 'companies',
    entityTypeId: 3,
  },
  contact: {
    entitiesType: 'contacts',
    name: 'Contact',
    code: 'contact',
    pluralCode: 'contacts',
    entityTypeId: 4,
  },
  companyLogo: {
    name: 'Company logo',
    code: 'company_logo',
    pluralCode: 'company_logos',
    entityTypeId: 5,
  },
  location: {
    name: 'Location',
    code: 'location',
    pluralCode: 'locations',
    entityTypeId: 6,
  },
  agent: {
    name: 'Agent',
    code: 'agent',
    pluralCode: 'agents',
    entityTypeId: 7,
  },
  cv: {
    name: 'Resume',
    code: 'cv',
    pluralCode: 'cv',
    entityTypeId: 8,
  },
  pipeline: {
    name: 'Pipeline',
    code: 'pipeline',
    pluralCode: 'pipelines',
    entityTypeId: 9,
    workflowTypeCode: workflowTypeCodes.pipelineFlow,
  },
  candidatePhoto: {
    name: 'Candidate photo',
    code: 'candidate_photo',
    pluralCode: 'candidate_photos',
    entityTypeId: 10,
  },
  cvInbox: {
    name: 'CvInbox',
    code: 'cv_inbox',
    pluralCode: 'cv_inboxes',
    entityTypeId: 11,
  },
  postmark: {
    name: 'Postmark',
    code: 'postmark',
    pluralCode: 'postmark',
    entityTypeId: 12,
  },
  nylas: {
    name: 'Nylas',
    code: 'nylas',
    pluralCode: 'nylas',
    entityTypeId: 13,
  },
  activityNote: {
    name: 'Activity note',
    code: 'activity_note',
    pluralCode: 'activity_notes',
    entityTypeId: 14,
  },
  skill: {
    name: 'Skill',
    code: 'skill',
    pluralCode: 'skills',
    entityTypeId: 15,
  },
  externalEmail: {
    name: 'External email',
    code: 'external_emails',
    pluralCode: 'externalEmails',
    entityTypeId: 16,
  },
  attachmentType: {
    name: 'Attachment type',
    code: 'attachment_types',
    pluralCode: 'attachmentTypes',
    entityTypeId: 17,
  },
  entityList: {
    name: 'Entity list',
    code: 'entity_lists',
    pluralCode: 'entityLists',
    entityTypeId: 18,
  },
  jobRequest: {
    name: 'Job Request',
    code: 'job_request',
    pluralCode: 'job_requests',
    entityTypeId: 19,
    workflowTypeCode: workflowTypeCodes.jobRequestFlow,
  },
};
export const bwfEntityTypesByEntityTypeId = keyBy(bwfEntityTypes, 'entityTypeId');
export const enWeekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const customFieldWidgetTypes = {
  input: 'input',
  multiselect: 'multiselect',
  rte: 'rte',
  stars: 'stars',
  total: 'total',
  skillsTaxonomy: 'skillsTaxonomy',
  binaryToggleInteger: 'binaryToggleInteger',
};

export const userAccountTypes = {
  agent: 'agent',
  contact: 'contact',
  candidate: 'candidate',
} as const;

export const openModal = 'entityModal=1&section=generalInformation';

export const GenericContext = React.createContext<Partial<GenericContextType>>({});

export const browserHistory = createBrowserHistory() as any;

export const maxSMSLength = 1600;

export const agentContactAccountTypes = [userAccountTypes.agent, userAccountTypes.contact];

export const appTypes = {
  candidate: 'candidate',
  agent: 'agent',
};

export const systemErrorCodes = ['system.quota-exceeded', 'system.nylas-sync-error', 'system.nylas-sync-disabled'];

export const socketEvents = {
  entityGroupCreated: 'entity-group-created',
  entityGroupUpdated: 'entity-group-updated',
  entityGroupDeleted: 'entity-group-deleted',

  bulkActionResponse: 'bulk-action-response',

  skillTaxonomyCreated: 'skill-taxonomy-created',
  skillTaxonomyDeleted: 'skill-taxonomy-deleted',
  skillTaxonomyUpdated: 'skill-taxonomy-updated',
  skillGroupCreated: 'skill-group-created',
  skillGroupDeleted: 'skill-group-deleted',
  skillGroupUpdated: 'skill-group-updated',
  skillCreated: 'skill-created',
  skillDeleted: 'skill-deleted',
  skillUpdated: 'skill-updated',
};

export const actionsInInfinitive: { [key in EntityActionName]?: string } = {
  unarchive: 'restore',
};

export const actionsInContinous: { [key in EntityActionName]: string } = {
  archive: 'archiving',
  unarchive: 'restoring',
  manualStatusChange: 'changing status',
};

export const actionsInPastSimple: { [key in EntityActionName]: string } = {
  archive: 'archived',
  unarchive: 'restored',
  manualStatusChange: 'status changed',
};

export const removeScriptRegex = /<script\b[^<]*(?:(?!<\/script\s*>)<[^<]*)*<\/script\s*>/gi;

export const userGroupCodes = {
  allAgents: 'allAgents',
  superAdmins: 'superAdmins',
};

export const builtInListsCodes = {
  archive: 'archive',
  builtIn: 'built-in',
};

export const accountTypeAccessPortal: any = {
  [userAccountTypes.agent]: userAccountTypes.agent,
  [userAccountTypes.contact]: userAccountTypes.agent,
  [userAccountTypes.candidate]: userAccountTypes.candidate,
};

export const portalsTypes = ['candidate', 'agent'] as const;

export const authMethods = {
  password: 'password',
  google: 'google',
  facebook: 'facebook',
  sso: 'sso',
} as const;

export const defaultQueryCacheTime = 60 * 1000;
